import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { GenerateIcon, PdfIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'

import { getRadPliTemplates, PliTemplatesData } from '../../../api/rads/get-rad-pli-templates'
import { printRadCenterPli } from '../../../api/rads/print-rad-center-pli'
import { printRadParticipantPli } from '../../../api/rads/print-rad-participant-pli'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

type PliTemplateRow = PliTemplatesData & { id: number }

type PrintCenterButtonProps = {
  pli?: {
    path?: string
    pliPrintedAt?: Date
    pliModifiedAt?: Date
  }
  radId: number
  onSuccess?: () => void
} & (
  | { type: 'center'; centerId: number; participantId?: never }
  | { type: 'participant'; participantId: number; centerId?: never }
)

const PrintPliButton = ({
  pli,
  type,
  radId,
  centerId,
  participantId,
  onSuccess
}: PrintCenterButtonProps) => {
  const { modalState, toggleModal, closeModel } = useModalManager(['print-center'])
  // the data table matches selected rows by id, so every template gets one up front
  const [selected, setSelected] = useState<PliTemplateRow[]>([])
  const [data, setData] = useState<PliTemplateRow[]>([])

  const { mutate: mutateCenter, isPending: isCenterPending } = useMutation({
    mutationFn: printRadCenterPli,
    onSuccess: () => {
      onSuccess?.()
      closeModel('print-center')
    },
    onError: showAxiosError
  })
  const { mutate: mutatePartiticpant, isPending: isParticipantPending } = useMutation({
    mutationFn: printRadParticipantPli,
    onSuccess: () => {
      onSuccess?.()
      closeModel('print-center')
    },
    onError: showAxiosError
  })

  useEffect(() => {
    const call = async () => {
      if (modalState['print-center']) {
        const res = await getRadPliTemplates(radId)
        const templates = res.map((item, index) => ({ ...item, id: index }))
        setData(templates)
        setSelected(templates)
      }
    }
    call()
  }, [modalState['print-center']])

  const columns: Column<PliTemplateRow>[] = [{ accessorKey: 'label', header: 'Dossiers' }]

  return (
    <div className="flex items-center gap-2 py-1">
      {pli?.path && pli.pliPrintedAt && (
        <div className="flex flex-col gap-1">
          <Typography.Link
            variant="tag"
            onClick={() => {
              window.open(pli.path, '_blank', 'noopener,noreferrer')
            }}
          >
            Pli Imprimé
          </Typography.Link>
          <Badge text={`Imprimé le ${DateFormatter.DDMMYYYY(pli.pliPrintedAt)}`} />
          {pli.pliModifiedAt && (
            <Badge text={`Modifié le ${DateFormatter.DDMMYYYY(pli.pliModifiedAt)}`} />
          )}
        </div>
      )}
      <Fragment>
        <Button
          variant="ghost"
          label="Générer le pli"
          icon={PdfIcon}
          onClick={() => toggleModal('print-center')}
        />

        <Modal
          isOpen={modalState['print-center']}
          onCancel={() => closeModel('print-center')}
          title="Générer le pli"
          titleAlignment="left"
          icon={GenerateIcon}
          hasBorderBottom
          isLoading={type === 'center' ? isCenterPending : isParticipantPending}
          actionButtonsOptions={{
            cancelText: 'Annuler',
            confirmText: 'Générer',
            onConfirm: () => {
              const templates = selected.map((s) => s.key)
              if (type === 'participant')
                return mutatePartiticpant({ id: radId, participantId, templates })
              if (type === 'center') return mutateCenter({ id: radId, centerId, templates })
            }
          }}
        >
          <div className={cn(MODAL_BODY_CLASSNAME, 'flex flex-col gap-4')}>
            <DataTable<PliTemplateRow>
              bodyVariant={'white'}
              data={data}
              columns={columns}
              rowCount={data.length}
              paginated={false}
              selectable={true}
              onSelectedDataChange={(input) => setSelected(input.rows ?? [])}
              selectedData={{ rows: selected, isAllSelected: false }}
            />
          </div>
        </Modal>
      </Fragment>
    </div>
  )
}

export default PrintPliButton
